import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useContent } from '../hooks/useContent';
import {
  verifyEmailToken,
  requestEmailConfirmation,
  getPendingEmail,
  rememberPendingEmail,
  clearPendingEmail,
} from '../services/accountService';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const verificationId = searchParams.get('verification-id');
  const [status, setStatus] = useState('loading');
  const [isResending, setIsResending] = useState(false);
  const [resendFeedback, setResendFeedback] = useState({ type: '', message: '' });
  const [email, setEmail] = useState(() => getPendingEmail());
  const hasAttemptedRef = useRef(false);
  const { isDark } = useTheme();
  const { content, loading: contentLoading } = useContent('verifyEmail');


  useEffect(() => {
    if (!verificationId) {
      setStatus('error');
      return;
    }

    if (hasAttemptedRef.current) {
      return;
    }
    hasAttemptedRef.current = true;

    const runVerification = async () => {
      setStatus('loading');
      try {
        await verifyEmailToken(verificationId);
        localStorage.setItem('quikr_email_verified', 'true');
        clearPendingEmail();
        setStatus('success');
      } catch (err) {
        setStatus('error');
      }
    };

    runVerification();
  }, [verificationId]);

  const handleResendVerification = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setResendFeedback({
        type: 'error',
        message: content.error.missingEmail,
      });
      return;
    }

    setIsResending(true);
    setResendFeedback({ type: '', message: '' });
    try {
      rememberPendingEmail(normalizedEmail);
      const data = await requestEmailConfirmation(normalizedEmail);
      setResendFeedback({
        type: 'success',
        message: data?.message || content.error.resendSuccess,
      });
    } catch (err) {
      setResendFeedback({
        type: 'error',
        message: err?.message || content.error.resendError,
      });
    } finally {
      setIsResending(false);
    }
  };

  const isSuccess = status === 'success';
  const isError = status === 'error';
  const isLoading = status === 'loading';

  if (contentLoading || !content) {
    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-no-repeat chameleon-background pointer-events-none"
        style={{
          backgroundImage: 'url(/images/Cameleon_HD.jpg)',
          backgroundPosition: 'calc(100% + 100px) center'
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-l from-dark-900/0 via-dark-900/70 to-dark-900 pointer-events-none"></div>
      <div className="relative max-w-md w-full mx-auto">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <img src="/images/logo.png" alt="Quikr.it Logo" className="w-40 h-20 object-contain" />
          </Link>
        </div>

        <div className="bg-dark-800 p-8 rounded-xl border border-dark-700 text-center space-y-4">
          {isLoading && (
            <>
              <h1 className="text-2xl font-semibold text-white">{content.pageTitle}</h1>
              <p className="text-gray-300">{content.loading}</p>
            </>
          )}

          {isSuccess && (
            <>
              <h1 className="text-2xl font-semibold text-white">{content.success.title}</h1>
              <p className="text-gray-300 text-lg">
		  {content.success.message}
              </p>
              <Link
                to="/login"
                className="inline-flex justify-center w-full py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors duration-200"
              >
		  {content.success.button}
              </Link>
            </>
          )}

          {isError && (
            <div className="space-y-4">
              <h1 className="text-2xl font-semibold text-white">{content.error.title}</h1>
              <p className="text-gray-300">
		  {content.error.message}
              </p>
              <div className="text-left space-y-2">
                <label htmlFor="resend-email" className="block text-sm font-medium text-gray-300">
		  {content.error.emailLabel}
                </label>
                <input
                  id="resend-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder={content.error.emailPlaceholder}
                />
              </div>
              {resendFeedback.message && (
                <p
                  className={`text-sm ${
                    resendFeedback.type === 'success' ? 'text-green-300' : 'text-red-300'
                  }`}
                >
                  {resendFeedback.message}
                </p>
              )}
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={isResending}
                className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isResending ? content.error.resending : content.error.resendButton }
              </button>
              <Link to="/login" className="block text-sm text-primary-300 hover:text-primary-200">
		  {content.error.backToLogin}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
